import React, { useEffect, useState } from 'react';
import { getWhishPayment } from '../billingApi';
import { useEntitlements } from '../contexts/EntitlementsContext';
import { useLanguage } from '../contexts/LanguageContext';
import './WhishPaymentStatus.css';

export default function WhishPaymentStatus({ user, paymentId, failedAttempt = false }) {
  const { refresh } = useEntitlements();
  const { language } = useLanguage();
  const ar = language === 'ar';
  const [status, setStatus] = useState('checking');
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [polling, setPolling] = useState(true);
  useEffect(() => {
    let live = true;
    let timer;
    const deadline = Date.now() + 6 * 60 * 1000;
    setStatus('checking');
    setError('');
    setPolling(true);
    function schedule() {
      if (live && Date.now() < deadline) timer = window.setTimeout(check, 3000);
      else if (live) setPolling(false);
    }
    async function check() {
      try {
        const result = await getWhishPayment(user, paymentId);
        if (!live) return;
        setError('');
        setStatus(result.status);
        if (['pending', 'unknown'].includes(result.status)) {
          schedule();
        } else {
          setPolling(false);
          if (['success', 'refunded'].includes(result.status)) {
            // A refresh problem must not overwrite an already verified outcome.
            try { await refresh(); } catch { /* Access can refresh independently. */ }
          }
        }
      } catch (e) {
        if (!live) return;
        // A transport error is not a new payment outcome.
        setStatus(previous => previous === 'checking' ? 'unknown' : previous);
        if ([401, 403, 404].includes(e.status)) {
          setError(e.message);
          setPolling(false);
        } else {
          // Callback processing and status polling may briefly overlap. Retry
          // temporary failures through the provider's full five-minute window.
          schedule();
        }
      }
    }
    void check();
    return () => { live = false; window.clearTimeout(timer); };
  }, [user, paymentId, refresh, attempt]);
  const copy = ar ? {
    checking: 'جارٍ التحقق من الدفع عبر Whish…', success: 'تم تأكيد الدفع عبر Whish وتحديث اشتراكك.',
    pending: 'لم يؤكد Whish الدفع بعد. يمكن إعادة المحاولة عبر رابط الدفع حتى انتهاء صلاحيته.',
    unknown: 'تعذر تأكيد حالة الدفع مع Whish حتى الآن.',
    failed: 'انتهت صلاحية رابط Whish دون دفع. يمكنك بدء عملية دفع جديدة.',
    refunded: 'تم رد هذه الدفعة عبر Whish وتحديث صلاحية الاشتراك.', rejected: 'تعذر إنشاء دفعة Whish. يرجى المحاولة مجدداً.',
  } : {
    checking: 'Verifying your Whish payment…', success: 'Whish payment verified. Your subscription has been updated.',
    pending: 'Whish has not confirmed payment yet. You can retry on the payment link until it expires.',
    unknown: 'We haven’t been able to confirm the payment status with Whish yet.',
    failed: 'This Whish payment link expired unpaid. You can start a new checkout.',
    refunded: 'This Whish payment was refunded and your access has been updated.', rejected: 'Whish could not create this payment. Please try again.',
  };
  const unresolved = ['checking', 'pending', 'unknown'].includes(status);
  const failedReturn = failedAttempt && unresolved;
  const titles = ar ? {
    checking: 'جارٍ التحقق من الدفع', pending: 'بانتظار تأكيد الدفع', unknown: 'بانتظار تأكيد الدفع',
    success: 'تم الدفع بنجاح', failed: 'لم يكتمل الدفع', refunded: 'تم رد الدفعة', rejected: 'تعذر بدء الدفع',
  } : {
    checking: 'Checking your payment', pending: 'Awaiting payment confirmation', unknown: 'Awaiting payment confirmation',
    success: 'Payment successful', failed: 'Payment not completed', refunded: 'Payment refunded', rejected: 'Checkout unavailable',
  };
  const title = error ? (ar ? 'تعذر التحقق من الدفع' : 'Unable to check payment')
    : failedReturn ? (ar ? 'لم تنجح محاولة الدفع' : 'Payment attempt unsuccessful')
      : titles[status] || titles.unknown;
  // The failure redirect describes an attempt, not the final provider status.
  // Keep that context even if the first status request times out.
  const description = failedReturn
    ? (status === 'pending'
      ? (ar ? 'لم تكتمل محاولة الدفع. يمكنك بدء دفع جديد بعد تأكيد Whish انتهاء صلاحية الرابط، ومدته خمس دقائق من وقت إنشائه.' : 'Your payment attempt did not complete. You can start a fresh checkout once Whish confirms this link has expired, up to five minutes after it was created.')
      : (ar ? 'نعمل على تأكيد الحالة النهائية لهذه الدفعة مع Whish.' : 'We’re confirming the final status of this attempt with Whish.'))
    : copy[status] || copy.unknown;
  return <div className="js-whish-status" role="status" aria-live="polite" aria-atomic="true">
    <h3 className="js-whish-status__title">{title}</h3>
    {!error && <p className="js-whish-status__description">{description}</p>}
    {polling && unresolved && <p className="js-whish-status__detail">
      {ar ? 'يتم التحقق تلقائياً؛ لا حاجة لتحديث الصفحة.' : 'Checking automatically. You don’t need to refresh.'}
    </p>}
    {!polling && !error && unresolved && <p className="js-whish-status__detail">
      {ar ? 'توقّف التحقق التلقائي. اضغط أدناه للتحقق مجدداً.' : 'Automatic checking has paused. Check again below for the latest payment status.'}
    </p>}
    {error && <p className="js-whish-status__description" role="alert">{error}</p>}
    {!polling && !error && unresolved && <button className="js-whish-status__retry" type="button" onClick={() => setAttempt(value => value + 1)}>{ar ? 'تحقق مجدداً' : 'Check payment again'}</button>}
  </div>;
}
